import styled, { css } from 'styled-components';

import Divider from '@/components/atom/Divider';
import FavoriteStar from '@/components/atom/icon/FavoriteStar';
import theme from '@/styles/theme';

export interface ListItemProps {
  doctorName: string;
  hospitalName: string;
  doctorProfileImageUrl: string;
  reviewRating: string;
  reviewTotalCount: number;
  videoCallAvailable: boolean;
  professionalDepartmentName?: string;
  isLastItem?: boolean;
}

const NeighborhoodSearchDoctorItem = ({
  doctorName,
  hospitalName,
  doctorProfileImageUrl,
  reviewRating,
  reviewTotalCount,
  videoCallAvailable,
  professionalDepartmentName,
  isLastItem,
}: ListItemProps) => {
  return (
    <>
      <Wrapper>
        <TopWrapper>
          <DoctorInfoWrapper>
            <Names>
              <DoctorName>{doctorName}</DoctorName>
              <HospitalName>{hospitalName}</HospitalName>
            </Names>
            {reviewTotalCount > 0 && (
              <Ratings>
                <FavoriteStar size={14} fullColor={theme.color.yellow500} />
                <Score>{reviewRating}</Score>
                <ReviewCount>({reviewTotalCount})</ReviewCount>
              </Ratings>
            )}
            {(videoCallAvailable || !!professionalDepartmentName) && (
              <Badges>
                {!!professionalDepartmentName && (
                  <ProfessionalDepartmentBadge>{professionalDepartmentName}전문의</ProfessionalDepartmentBadge>
                )}
                {videoCallAvailable && <VideoCallBadge>영상진료</VideoCallBadge>}
              </Badges>
            )}
          </DoctorInfoWrapper>
          {doctorProfileImageUrl && (
            <ImageWrapper>
              <DoctorImage backgroundImage={doctorProfileImageUrl} />
              <ImageBlur />
            </ImageWrapper>
          )}
        </TopWrapper>
      </Wrapper>
      {!isLastItem && (
        <DividerWrapper>
          <InnerDivider height={'1px'} color={theme.color.gray40} />
        </DividerWrapper>
      )}
    </>
  );
};

export default NeighborhoodSearchDoctorItem;

const Wrapper = styled.li`
  position: relative;
  padding: 8px 0;
  display: flex;
  flex-direction: column;
`;

const TopWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-content: center;
  gap: 16px;
  padding: 0 16px 0 22px;
`;

const DoctorInfoWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const Names = styled.div`
  display: flex;
  align-items: center;
  height: 24px;
  gap: 4px;
`;

const DoctorName = styled.h3`
  ${({ theme }) => css`
    ${theme.typography.UI16SB}
    color: ${theme.color.gray600};
  `}
`;

const HospitalName = styled.p`
  ${({ theme }) => css`
    display: flex;
    ${theme.typography.UI13M}
    color: ${theme.color.gray300};
  `}
`;

const Ratings = styled.div`
  display: flex;
  white-space: nowrap;
  align-items: center;
  margin-top: 8px;
`;

const ImageWrapper = styled.div`
  position: relative;
`;

const ImageBlur = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 128px;
  height: 21.333px;
  background: linear-gradient(0deg, #fff 0%, rgba(255, 255, 255, 0) 100%);
`;

const DoctorImage = styled.div<{ backgroundImage?: string }>`
  width: 128px;
  height: 128px;
  background-size: cover;
  ${({ backgroundImage }) => backgroundImage && `background-image: url(${backgroundImage});`}
`;

const InnerDivider = styled(Divider)`
  width: 100%;
`;

const DividerWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-content: center;
`;

const Score = styled.span`
  ${({ theme }) => `
    ${theme.typography.UI13M}
    color: ${theme.color.gray600};
  `}
  margin-left: 4px;
`;

const ReviewCount = styled.span`
  ${({ theme }) => `
    ${theme.typography.UI13M}
    color: ${theme.color.gray300};
  `}
  margin-left: 1px;
`;

const Badges = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 12px;
`;

const ProfessionalDepartmentBadge = styled.span`
  display: inline-flex;
  padding: 1px 4px;
  border-radius: 2px;
  align-items: center;
  ${({ theme }) => css`
    ${theme.typography.UI12M}
    color: ${theme.color.blue600};
    background-color: ${theme.color.blue100};
  `}
`;

const VideoCallBadge = styled(ProfessionalDepartmentBadge)`
  ${({ theme }) => css`
    color: ${theme.color.gray300};
    background-color: ${theme.color.gray30};
  `}
`;
